import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Commands, Results, Search } from '@local/client-contracts';
import { UiIconModel } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { getFileType } from '@shared/consts/file-types';
import { FlagsService } from '@shared/services/flags.service';
import { CustomKeyboardEvent, KeyboardService } from '@shared/services/keyboard.service';
import { copyToClipboard } from '@shared/utils';
import { getModifiers, isEnterKey, isKey, keyCodes, KeyName } from '@local/ts-infra';
import { filter, Subscription } from 'rxjs';
import { HubService } from '../../../services/hub.service';
import { PreviewMode } from '../model/preview-mode';
import { BasePreviewComponent } from './components/base-preview/base-preview.component';
import { CodePreviewComponent } from './components/code-preview/code-preview.component';
import { ImagePreviewComponent } from './components/image-preview/image-preview.component';
import { MdPreviewComponent } from './components/md-preview/md-preview.component';
import { MediaPreviewComponent } from './components/media-preview/media-preview.component';
import { OnlinePreviewComponent } from './components/online-preview/online-preview.component';
import { PdfPreviewComponent } from './components/pdf-preview/pdf-preview.component';
import { TextPreviewComponent } from './components/text-preview/text-preview.component';
import { FilePreviewBase, FilePreviewType, ResultPreviewData } from './file-preview-types';
import { FilePreviewService } from './services/file-preview.service';
import { ErrorType } from '@shared/services/stream-content.service';
import { StaticCollectionResultItem } from '@local/client-contracts/src/search';

export type PreviewItemModel = {
  item: ResultPreviewData;
  fileType: string;
  icon: UiIconModel;
};

@UntilDestroy()
@Component({
  selector: 'file-preview',
  templateUrl: './file-preview.component.html',
  styleUrls: ['./file-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilePreviewComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('resultPreviewContainer', { read: ViewContainerRef }) container: ViewContainerRef;
  @HostBinding('style.overflow')
  overflow() {
    return this.previewMode !== 'popup' ? 'hidden' : 'unset';
  }
  readonly FOOTER_MASSAGE_WIDTH = 40;
  readonly FOOTER_PADDING = 20;

  @Output() size = new EventEmitter<PreviewMode>();
  @Output() download = new EventEmitter<Commands.DragCommand>();
  @Output() loaded = new EventEmitter<any>();
  @Output() open = new EventEmitter<any>();

  @Input() model: Search.ResultResourceItem;
  @Input() icon: UiIconModel;
  @Input() token: string;
  @Input() isPopup: boolean;
  @Input() smallWidthPreview = true;

  private _previewMode: PreviewMode;
  @Input() set previewMode(val: PreviewMode) {
    this._previewMode = val;
    this.width = 0;
    if (this.componentRef) {
      this.componentRef.instance.previewMode = this._previewMode;
    }
    this.updatePreviewWidth();
    this.hubService.changeFocusState(val !== 'popup', false);
    this.cdr.markForCheck();
  }
  get previewMode() {
    return this._previewMode;
  }

  @Input() set showBar(val: boolean) {
    this._showBar = val && this.shouldShowBar(this.resultPreview?.model?.resource?.traits?.mimeType);
  }

  get showBar(): boolean {
    return this._showBar;
  }

  fileType: FilePreviewType;
  errorType: ErrorType;
  currentPage: number;
  totalPages: number;

  private _showBar = true;
  private isReload = false;
  private readonly minWidth = 0;
  private readonly maxWidth = 100;
  private width = 0;
  private resultPreview: ResultPreviewData;
  private componentRef: ComponentRef<FilePreviewBase>;
  private keyHandlerId: string;
  private previewAble: boolean;

  get footerWidth() {
    let footerWidth = 0;
    const traits = this.model?.resource?.traits;
    if (!traits) return footerWidth;

    if (traits.createdAt || traits.createdBy?.name) footerWidth = this.FOOTER_MASSAGE_WIDTH;
    if (traits.modifiedAt || traits.modifiedBy?.name) footerWidth += this.FOOTER_MASSAGE_WIDTH;
    return footerWidth + this.FOOTER_PADDING;
  }

  get footerHeight() {
    let footerHeight = 0;
    const traits = this.model?.resource?.traits;
    if (!traits) return footerHeight;

    if (traits.createdAt || traits.createdBy?.name) footerHeight = this.FOOTER_MASSAGE_WIDTH;
    if (traits.modifiedAt || traits.modifiedBy?.name) footerHeight += this.FOOTER_MASSAGE_WIDTH;
    return footerHeight;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private resultPreviewService: FilePreviewService,
    private keyboardService: KeyboardService,
    private hubService: HubService,
    private flagsService: FlagsService
  ) {}

  ngOnInit(): void {
    if (this.keyHandlerId) return;
    this.keyHandlerId = this.keyboardService.registerKeyHandler((keys, event) => this.handleKeys(keys, event), 9);
  }

  ngAfterViewInit(): void {
    this.resultPreviewService.preview$
      .pipe(
        untilDestroyed(this),
        filter((val) => !!val)
      )
      .subscribe((val) => {
        this.resultPreview = val;
        this.isReload = false;
        this.loadPreview();
        this.cdr.markForCheck();
      });
  }

  private handleKeys(keys: Array<KeyName>, event: CustomKeyboardEvent) {
    if (this.previewMode === 'none') return;
    const modifiers = getModifiers(keys);

    if (isKey(event, keyCodes.escape)) {
      this.size.emit('previous');
      event.stopPropagation();
    }
    if (isKey(event, keyCodes.equal) || isKey(event, keyCodes.numpadadd)) {
      this.zoomIn();
      event.stopPropagation();
    }
    if (isKey(event, keyCodes.minus) || isKey(event, keyCodes.numpadsubtract)) {
      this.zoomOut();
      event.stopPropagation();
    }
    if (modifiers.length === 1 && modifiers[0] === keyCodes.commandOrControl.toLowerCase()) {
      if (isKey(event, keyCodes.d)) {
        this.download.emit((this.resultPreview.model.view.title as Results.Title).onDrag);
        event.preventDefault();
        event.stopPropagation();
      }
      if (isKey(event, keyCodes.c)) {
        copyToClipboard((this.model?.view?.title?.onClick as Commands.OpenUrl)?.url);
        event.preventDefault();
        event.stopPropagation();
      }
    }
    if (isEnterKey(keys[0])) {
      if (!this.previewAble) {
        event.stopPropagation();
      }
    }
  }

  shouldShowBar(mimeType) {
    return !['video', 'audio', 'online'].includes(getFileType(mimeType || ''));
  }

  private async loadPreview() {
    let isEnable = true;
    if (
      this.resultPreview?.model.link &&
      (this.resultPreview.model as any as StaticCollectionResultItem)?.kind !== 'static-collection-item'
    ) {
      isEnable = await this.flagsService.isEnabled('filePreviews', this.resultPreview.model?.link.id);
    }
    const mimeType = this.resultPreview?.model?.resource?.traits?.mimeType;
    this.fileType = getFileType(mimeType || '', this.resultPreview?.model?.resource?.name);
    this.previewAble = true;
    if (this.showBar) {
      this.showBar = this.shouldShowBar(mimeType);
    }
    this.errorType = null;

    if (this.componentRef) {
      this.resultPreviewService.changePreviewVisibility(this.componentRef, false);
    }
    this.componentRef = this.createPreviewComponent(isEnable ? this.fileType : null);
    const subscriptions: Subscription[] = [
      this.componentRef.instance.open.pipe(untilDestroyed(this)).subscribe(() => this.open.emit()),
      this.componentRef.instance.reload.pipe(untilDestroyed(this)).subscribe(() => this.reload()),
      this.componentRef.instance.download.pipe(untilDestroyed(this)).subscribe(() => this.download.emit()),
      this.componentRef.instance.totalPages.pipe(untilDestroyed(this)).subscribe((totalPages) => this.updateTotalPages(totalPages)),
      this.componentRef.instance.currentPage.pipe(untilDestroyed(this)).subscribe((currentPage) => this.updateCurrentPage(currentPage)),
    ];
    this.componentRef.instance.show = true;
    this.componentRef.instance.isRestricted = !isEnable;
    this.componentRef.instance.token = this.token;
    this.componentRef.instance.footerWidth = this.footerWidth;
    this.componentRef.instance.footerHeight = this.footerHeight;
    this.componentRef.instance.model = {
      item: this.resultPreview,
      fileType: this.fileType,
      icon: this.icon,
    };
    this.componentRef.instance.previewMode = this._previewMode;
    if (this.fileType) {
      this.resultPreviewService.updatePreviewsCache(
        this.resultPreview.model.id,
        this.resultPreview.srcUrl,
        this.componentRef,
        subscriptions
      );
    }
    this.loaded.emit();
    this.cdr.markForCheck();
  }

  private createPreviewComponent(type: FilePreviewType): ComponentRef<FilePreviewBase> {
    this.container.clear();
    switch (type) {
      case 'video':
      case 'audio':
        return this.container.createComponent(MediaPreviewComponent);
      case 'image':
        return this.container.createComponent(ImagePreviewComponent);
      case 'text':
      case 'html':
        return this.container.createComponent(TextPreviewComponent);
      case 'pdf':
        return this.container.createComponent(PdfPreviewComponent);
      case 'md':
        return this.container.createComponent(MdPreviewComponent);
      case 'online':
        return this.container.createComponent(OnlinePreviewComponent);
      case 'code':
        return this.container.createComponent(CodePreviewComponent);
      default:
        return this.container.createComponent(BasePreviewComponent);
    }
  }

  private loadFinished(error?: ErrorType) {
    this.errorType = error ?? null;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.resultPreviewService.clearCache();
    this.resultPreviewService.preview$.next(null);
    this.container.clear();
    if (this.keyHandlerId) {
      this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
      this.keyHandlerId = null;
    }
  }

  reload() {
    this.fileType = null;
    this.isReload = true;
    this.resultPreviewService.removeFromCache(this.resultPreview.model.id);
    this.loadPreview();
  }

  zoomOut() {
    if (this.width - 10 < this.minWidth || !this.componentRef) return;
    this.width -= 10;
    this.updatePreviewWidth();
  }

  zoomIn() {
    if (this.width + 10 >= this.maxWidth || !this.componentRef) return;
    this.width += 10;
    this.updatePreviewWidth();
  }

  resetZoom() {
    this.width = 0;
    this.updatePreviewWidth();
  }

  private updatePreviewWidth() {
    if (!this.componentRef) return;
    this.componentRef.instance.prevWidth = this.width;
    this.cdr.markForCheck();
  }

  private updateTotalPages(totalPages: number) {
    this.totalPages = totalPages;
    this.cdr.markForCheck();
  }

  private updateCurrentPage(currentPage: number) {
    this.currentPage = currentPage;
    this.cdr.markForCheck();
  }
}
